// Cyrqle i18n - ES (canonical), EN, FR, CA
// All copy is editable here. Keep keys consistent across languages.

export const LANGS = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "ca", label: "Català" },
];

const es = {
  nav: {
    howItWorks: "Cómo funciona",
    science: "Ciencia",
    labs: "Laboratorios",
    faq: "Preguntas",
    upload: "Subir análisis",
    login: "Entrar",
  },
  hero: {
    overline: "Bienestar para tu círculo",
    titleA: "Cuida la salud",
    titleB: "de los que más quieres",
    subtitle:
      "Sube el análisis de tu familia y, en menos de 3 minutos, recibe una lectura clara: biomarcadores, hábitos y recomendaciones de bienestar para acompañar a tu círculo con confianza.",
    primaryCta: "Subir informe",
    secondaryCta: "Ver demo",
    uploadHint: "PDF · Hasta 10 MB · Echevarne, Pasteur o Álvarez",
    rotators: [
      "Para tu pareja, tus padres, tus hijos.",
      "Sin otro laboratorio.",
      "Sin lenguaje médico opaco.",
    ],
    trust: "Familias y profesionales del bienestar ya usan Cyrqle para cuidar mejor a su círculo.",
    compatible: "Compatible con informes de cualquier laboratorio",
    highlights: [
      { title: "Sube su análisis", subtitle: "PDF de quien quieres cuidar" },
      { title: "Lectura en minutos", subtitle: "Claridad para actuar en su bienestar" },
      { title: "Cualquier laboratorio", subtitle: "Con el análisis que ya tienen" },
    ],
  },
  partners: {
    subtitle: "Compatible con informes de cualquier laboratorio",
  },
  reportDetails: {
    overline: "Para tu familia",
    title: "Lo que obtienes para cuidar de los tuyos",
    description:
      "Interpretamos los análisis de tu círculo y convertimos los resultados en información clara para mejorar su bienestar y sus hábitos día a día.",
    indicated: "Los datos ya están ahí. Cyrqle te ayuda a cuidar mejor de quienes te importan.",
    stat1Value: "< 3",
    stat1Label: "minutos para tu informe",
    stat2Value: "8+",
    stat2Label: "categorías de salud",
    stat3Value: "100%",
    stat3Label: "compatible con cualquier lab",
    systemsTitle: "Categorías analizadas",
    systems: [
      "Salud metabólica",
      "Salud cardiovascular",
      "Función renal",
      "Función hepática",
      "Inflamación",
      "Vitaminas",
      "Salud hormonal",
      "Salud ósea",
    ],
    cta: "Subir mi análisis",
  },
  productPreview: {
    overline: "Vista previa",
    title: "Su salud, interpretada para que puedas cuidarla",
    subtitle:
      "Biomarcadores agrupados, hallazgos clave y recomendaciones de bienestar para acompañar a tu familia con decisiones concretas.",
    quote:
      "Cyrqle traduce el lenguaje del laboratorio en hábitos, nutrición y bienestar para el círculo que más te importa.",
    quoteAuthor: "Equipo Cyrqle",
  },
  whatsIncluded: {
    overline: "Qué incluye",
    title: "Todo para el bienestar de tu círculo",
    subtitle: "y para actuar con claridad desde el primer informe.",
    cta: "Subir un análisis",
    features: [
      {
        title: "Análisis de tu familia",
        description: "Sube PDF de cualquier laboratorio. Interpretamos los biomarcadores de quien quieres cuidar.",
      },
      {
        title: "Panorama de bienestar",
        description: "Edad biológica, estado general y áreas donde tu círculo puede mejorar hábitos.",
      },
      {
        title: "Hallazgos que importan",
        description: "Lo esencial de cada análisis, explicado para que puedas acompañar mejor a los tuyos.",
      },
      {
        title: "Hábitos personalizados",
        description: "Recomendaciones de estilo de vida, nutrición, ejercicio y suplementación para tu familia.",
      },
      {
        title: "Acompañamiento en tu cuidado",
        description: "Andy, tu copiloto Cyrqle, te ayuda a entender cada informe y qué hacer por ellos.",
      },
    ],
  },
  ctaBanner: {
    title: "Sube el análisis de quien quieres cuidar y descubre cómo mejorar su bienestar.",
    cta: "Empezar ahora",
    imageAlt: "Familia en un entorno luminoso, sensación de cuidado y bienestar compartido.",
  },
  upload: {
    title: "Sube tu informe",
    subtitle: "Arrastra el PDF aquí o selecciónalo desde tu dispositivo.",
    drop: "Suelta el PDF para empezar",
    choose: "Elegir archivo",
    accepted: "Solo PDF · Máx. 10 MB",
    parsing: "Analizando tu informe…",
    invalidType: "Solo aceptamos archivos PDF.",
    tooLarge: "El archivo supera los 10 MB.",
    networkError: "No hemos podido conectar con el servidor.",
    corsHint:
      "Tu navegador está bloqueando la conexión (CORS). Puedes probar el modo demo para ver una interpretación de ejemplo.",
    timeout: "El informe es grande, sigue procesándose. Inténtalo de nuevo en unos segundos.",
    unsupportedLab:
      "Aún no reconocemos este laboratorio. Hoy soportamos Echevarne, Pasteur y Álvarez — añadiremos más muy pronto.",
    tryDemo: "Probar con datos demo",
    cancel: "Cancelar",
    retry: "Reintentar",
    privacy: "Tu informe se procesa de forma cifrada y no se almacena sin tu consentimiento.",
  },
  analyzing: {
    title: "Leyendo tu informe",
    phases: [
      "Leyendo el informe…",
      "Detectando biomarcadores…",
      "Analizando tus biomarcadores…",
      "Generando recomendaciones personalizadas…",
    ],
    note: "Esto tarda unos segundos. No cierres la pestaña.",
  },
  results: {
    backToTop: "Volver arriba",
    newAnalysis: "Subir otro análisis",
    download: "Descargar resumen",
    summary: {
      overline: "Resumen ejecutivo",
      titleHello: "Tu lectura, en una página",
      titleSub: "Lo más relevante de tu análisis, sin tecnicismos.",
      labDetected: "Laboratorio detectado",
      biomarkers: "Biomarcadores leídos",
      inRange: "Dentro de rango",
      altered: "Alterados",
      keyFindings: "Hallazgos clave",
      healthSnapshot: "Estado general",
      healthSnapshotHint:
        "Indicador derivado del porcentaje de biomarcadores dentro de rango. Una valoración completa requiere cuenta.",
      patient: "Paciente",
      record: "Nº de expediente",
    },
    biomarkers: {
      overline: "Biomarcadores",
      title: "Tus biomarcadores, agrupados",
      subtitle: "Filtra, busca y entiende cada valor en su contexto.",
      search: "Buscar biomarcador…",
      onlyAltered: "Solo alterados",
      all: "Todos",
      empty: "No hay biomarcadores que coincidan.",
      ref: "Rango de referencia",
      normal: "Normal",
      high: "Alto",
      low: "Bajo",
    },
    algorithms: {
      overline: "Evaluación de bienestar",
      title: "Lo que dicen los especialistas",
      subtitle:
        "Nueve algoritmos especializados interpretan tus datos en áreas concretas de bienestar.",
      lifestyle: "Estilo de vida",
      nutrition: "Nutrición",
      supplementation: "Suplementación",
      clinical: "Seguimiento",
      exclusion: "A evitar",
      noRecs: "Sin recomendaciones específicas.",
      seeMore: "Ver más",
      seeLess: "Ver menos",
    },
    categories: {
      hematology: "Hematología",
      lipids: "Lípidos",
      glucose: "Glucosa",
      liver: "Función hepática",
      kidney: "Función renal",
      minerals: "Minerales",
      vitamins: "Vitaminas",
      thyroid: "Tiroides",
      other: "Otros",
    },
    algoNames: {
      glucose: "Glucosa",
      liver: "Hígado",
      anemia: "Anemia",
      cametabolism: "Metabolismo del calcio",
      cholesterol: "Colesterol",
      magnesium: "Magnesio",
      zinc: "Zinc",
      vitaminB: "Vitaminas del grupo B",
      vitaminD: "Vitamina D",
    },
    disclaimer:
      "Estas recomendaciones no sustituyen la consulta con tu profesional sanitario.",
  },
  conversion: {
    title: "Cuidar de tu círculo empieza aquí",
    subtitle:
      "Crea tu cuenta gratis para guardar los informes de tu familia, compararlos en el tiempo y construir hábitos de bienestar para todos.",
    bullets: [
      "Guarda los análisis de tu círculo",
      "Compara biomarcadores en el tiempo",
      "Seguimiento de bienestar y edad biológica",
      "Recomendaciones y recordatorios para tu familia",
      "Acceso al equipo Cyrqle",
    ],
    primary: "Crear cuenta gratis",
    secondary: "Quizá más tarde",
    saved: "El informe quedará guardado en tu cuenta para seguir cuidando de tu círculo.",
  },
  how: {
    overline: "Cómo funciona",
    title: "Del análisis de tu familia a hábitos de bienestar en tres pasos",
    steps: [
      {
        n: "01",
        t: "Subes su informe",
        d: "El PDF del laboratorio de quien quieres cuidar. Sin nuevas extracciones.",
      },
      {
        n: "02",
        t: "Leemos cada biomarcador",
        d: "Detectamos automáticamente cada valor, su unidad y su rango de referencia.",
      },
      {
        n: "03",
        t: "Te orientamos para cuidar",
        d: "Especialistas convierten los datos en hábitos y bienestar claros para tu círculo.",
      },
    ],
  },
  trust: {
    overline: "Por qué Cyrqle",
    title: "Sus datos. Tu cuidado.",
    sub: "Cyrqle pone la información de salud de tu familia al alcance de quien más les cuida: tú. Transparente, revisado por profesionales del bienestar.",
    stats: [
      { value: "85%", label: "entienden mejor la salud de su círculo" },
      { value: "9", label: "algoritmos especializados" },
      { value: "< 3 min", label: "para interpretar cada informe" },
    ],
    pillars: [
      {
        t: "Validado por especialistas",
        d: "Cada algoritmo está construido y revisado por profesionales sanitarios.",
      },
      {
        t: "Para toda la familia",
        d: "Un análisis por persona, un mismo lugar para cuidar de tu círculo.",
      },
      {
        t: "Diseñado para cuidar",
        d: "Lenguaje claro para que sepas qué hacer por ellos, sin tecnicismos.",
      },
    ],
  },
  labs: {
    overline: "Compatibilidad",
    title: "Laboratorios reconocidos hoy",
    sub: "Detectamos automáticamente el formato. Pronto ampliaremos a más laboratorios y a fotos del informe.",
    soon: "Próximamente",
    names: ["Echevarne", "Pasteur", "Álvarez", "+ más laboratorios", "Fotos JPG/PNG", "OCR móvil"],
  },
  faq: {
    overline: "Preguntas frecuentes",
    title: "Antes de subir un análisis de tu familia",
    items: [
      {
        q: "¿Es seguro subir el análisis de mi familia?",
        a: "Sí. La conexión es cifrada y el PDF no se almacena sin tu consentimiento. Sin cuenta, los datos viven solo en tu navegador.",
      },
      {
        q: "¿Puedo subir análisis de varias personas?",
        a: "Sí. Con tu cuenta puedes guardar y seguir los informes de tu círculo: pareja, padres, hijos.",
      },
      {
        q: "¿Qué laboratorios admiten hoy?",
        a: "Hoy detectamos automáticamente Echevarne, Pasteur y Álvarez. Estamos ampliando la cobertura cada mes.",
      },
      {
        q: "¿Sustituye al médico de mi familia?",
        a: "No. Cyrqle interpreta los datos para el bienestar; las decisiones clínicas siempre las toma su profesional sanitario.",
      },
      {
        q: "¿Por qué necesito cuenta?",
        a: "Para guardar los informes de tu círculo, compararlos y recibir recomendaciones de hábitos y bienestar.",
      },
    ],
  },
  supplements: {
    overline: "Bienestar para tu círculo",
    title: "Suplementación afinada para ellos",
    subtitle: "Selección de productos que conecta con los hallazgos del informe. Sin promesas mágicas: nutrientes con respaldo, dosis claras y trazabilidad para cuidar mejor.",
    curated: "Selección curada",
    unlockTitle: "Construye tu rutina completa",
    unlockSub: "Crea tu cuenta para ver más opciones, comparar marcas y recibir un protocolo paso a paso.",
    unlockCta: "Desbloquear catálogo completo",
    disclaimer:
      "Cyrqle no vende ni receta medicamentos. Las sugerencias son orientativas y nunca sustituyen la valoración de un profesional sanitario.",
    reasons: {
      glucose: "Para tu glucosa basal elevada",
      cholesterol: "Para optimizar tu perfil lipídico",
      vitaminD: "Para corregir tu déficit de vitamina D",
      magnesium: "Para tu magnesio en límite inferior",
      zinc: "Para tu zinc plasmático bajo",
      anemia: "Para apoyar tu serie roja y reservas de hierro",
      liver: "Para apoyar tu función hepática",
      vitaminB: "Para mantener tus vitaminas B",
      cametabolism: "Para tu salud ósea y metabolismo del calcio",
      foundation: "Stack básico recomendado",
    },
  },
  emailCapture: {
    overline: "Bienestar en tu círculo",
    title: "Tu familia, más sana cada semana.",
    subtitle:
      "Déjanos tu email o descarga la app y empezaremos a enviarte recomendaciones de suplementación, nutrición, deporte y bienestar — basadas en los informes de tu círculo.",
    pillars: ["Suplementación", "Nutrición", "Deporte", "Bienestar"],
    emailLabel: "Tu email",
    emailPlaceholder: "tu@correo.com",
    submit: "Quiero recibirlas",
    appOptIn: "Sí, quiero descargar la app",
    appOptInSub: "Recibirás un enlace para iOS y Android con tu cuenta vinculada.",
    privacy: "Sin spam. Puedes darte de baja en un clic. Los informes de tu familia siguen siendo tuyos.",
    invalid: "Email no válido.",
    networkError: "No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos segundos.",
    success: "Listo. Te hemos guardado en la lista.",
    successTitle: "Estás dentro.",
    successBody: "Te escribiremos pronto con tu primera recomendación personalizada.",
  },
  savedBanner: {
    title: "Tienes un análisis guardado",
    savedAgo: "hace",
    resume: "Ver el informe",
    resumeShort: "Ver informe",
  },
  chat: {
    launcherLabel: "Habla con Andy",
    title: "Andy · tu copiloto Cyrqle",
    subtitle: "Pregúntame sobre la salud de tu círculo y hábitos de bienestar",
    subtitleAnalysis: "Conozco el último informe de tu familia — pregúntame lo que necesites",
    placeholder: "Escribe tu pregunta…",
    send: "Enviar",
    reset: "Reiniciar conversación",
    askChip: "Preguntar a Andy",
    greetingOverline: "Empezar",
    greeting: "Hola, soy Andy.",
    greetingWithAnalysis: "Hola, soy Andy. Ya he leído el último informe de tu familia.",
    greetingBody:
      "Pregúntame por un biomarcador, pídeme hábitos para tu círculo o cuéntame a quién quieres cuidar.",
    greetingBodyAnalysis:
      "Puedo resumir los hallazgos, explicar un valor concreto o proponer un plan de bienestar de 4 semanas. ¿Por dónde empezamos?",
    suggestions: [
      "Resume este análisis",
      "¿Qué priorizo para cuidar?",
      "Plan de bienestar 4 semanas",
      "¿Qué significa el LDL alto?",
    ],
    disclaimer:
      "Andy es un asistente orientativo. No sustituye la consulta de un profesional sanitario.",
    errors: {
      network: "No hemos podido conectar con Andy. Inténtalo de nuevo.",
    },
    prefill: {
      biomarker: "Cuéntame sobre mi {name}: estoy en {value}{unit} (rango {range}). ¿Qué significa y qué puedo hacer?",
      algorithm: "Explícame mi evaluación de {name} (código: {code}). ¿Qué cambios prioritarios me recomiendas?",
      supplement: "¿Por qué me recomiendas {name}? ¿Cómo y cuándo lo tomo?",
    },
  },
  profileModal: {
    title: "Cuéntanos un poco sobre ti",
    subtitle:
      "Solo necesitamos tu edad y, opcionalmente, tu sexo biológico para calcular tu edad biológica y personalizar la lectura.",
    ageLabel: "Tu edad",
    sexLabel: "Sexo biológico",
    sexMale: "Hombre",
    sexFemale: "Mujer",
    sexOther: "Otro / Prefiero no decir",
    save: "Calcular",
    skip: "Ahora no",
    ageInvalid: "Introduce una edad entre 15 y 100.",
    privacy: "Guardado solo en tu navegador. No lo enviamos a ningún servidor.",
  },
  bioAge: {
    overline: "Edad biológica",
    biological: "Biológica",
    real: "Real",
    deltaYounger: "{years} años por debajo de tu edad real",
    deltaOlder: "{years} años por encima de tu edad real",
    deltaEqual: "Alineada con tu edad real",
    methodology:
      "Estimación basada en tus biomarcadores principales (LDL, glucosa, GGT, vitamina D, hierro, magnesio, hemoglobina). Es orientativa.",
    emptyTitle: "Conoce tu edad biológica",
    emptyBody:
      "Compara tu edad real con la que indican tus biomarcadores. Solo necesitamos tu edad.",
    computeCta: "Añadir mi edad",
    edit: "Editar mis datos",
  },
  summary: {
    overline: "Resumen del análisis",
    title: "Tu lectura, en una página",
    placeholder:
      "Aún no hay un resumen automático para este informe. Revisa los biomarcadores y la evaluación de bienestar más abajo, o pregúntale a Andy.",
  },
  knowledge: {
    overline: "Centro de conocimiento",
    title: "Aprende para cuidar mejor de tu círculo",
    subtitle:
      "Artículos breves sobre suplementación, nutrición, deporte, bienestar y la ciencia detrás de los biomarcadores de tu familia.",
    readMore: "Leer artículo",
    readMins: "min",
    categories: {
      all: "Todo",
      supplementation: "Suplementación",
      nutrition: "Nutrición",
      sport: "Deporte",
      wellness: "Bienestar",
      science: "Ciencia",
    },
  },
  article: {
    back: "Volver al centro de conocimiento",
    loading: "Cargando artículo…",
    notFound: "No encontramos este artículo.",
    references: "Referencias",
  },
  footer: {
    tagline: "Bienestar para tu familia.",
    legal: "Aviso legal",
    privacy: "Privacidad",
    terms: "Términos",
    support: "Soporte",
    rights: "Todos los derechos reservados.",
    disclaimer:
      "Cyrqle ofrece información orientativa basada en biomarcadores para el bienestar de tu círculo. No es un dispositivo médico ni sustituye la consulta profesional.",
  },
};

const en = {
  nav: { howItWorks: "How it works", science: "Science", labs: "Labs", faq: "FAQ", upload: "Upload report", login: "Sign in" },
  hero: {
    overline: "Wellness for your circle",
    titleA: "Care for the health",
    titleB: "of those you love",
    subtitle:
      "Upload your family's blood test and, in under 3 minutes, get a clear reading: biomarkers, habits and wellness recommendations to support your circle with confidence.",
    primaryCta: "Upload report",
    secondaryCta: "See demo",
    uploadHint: "PDF · Up to 10 MB · Echevarne, Pasteur or Álvarez",
    rotators: ["For your partner, parents, children.", "No new lab visit.", "No opaque medical jargon."],
    trust: "Families and wellness professionals use Cyrqle to care better for their circle.",
    compatible: "Works with reports from any laboratory",
    highlights: [
      { title: "Upload their report", subtitle: "PDF of who you want to care for" },
      { title: "Reading in minutes", subtitle: "Clarity to act on their wellness" },
      { title: "Any laboratory", subtitle: "With the test they already have" },
    ],
  },
  partners: {
    subtitle: "Works with reports from any laboratory",
  },
  reportDetails: {
    overline: "For your family",
    title: "What you get to care for your loved ones",
    description:
      "We interpret your circle's blood tests and turn results into clear information to improve their wellness and habits day by day.",
    indicated: "The data is already there. Cyrqle helps you care better for those who matter.",
    stat1Value: "< 3",
    stat1Label: "minutes to your report",
    stat2Value: "8+",
    stat2Label: "health categories",
    stat3Value: "100%",
    stat3Label: "works with any lab",
    systemsTitle: "Categories analyzed",
    systems: [
      "Metabolic health",
      "Cardiovascular health",
      "Kidney function",
      "Liver function",
      "Inflammation",
      "Vitamins",
      "Hormonal health",
      "Bone health",
    ],
    cta: "Upload my analysis",
  },
  productPreview: {
    overline: "Preview",
    title: "Their health, interpreted so you can care",
    subtitle:
      "Grouped biomarkers, key findings and wellness recommendations to support your family with concrete decisions.",
    quote:
      "Cyrqle translates lab language into habits, nutrition and wellness for the circle that matters most to you.",
    quoteAuthor: "Cyrqle team",
  },
  whatsIncluded: {
    overline: "What's included",
    title: "Everything for your circle's wellness",
    subtitle: "and to act with clarity from the first report.",
    cta: "Upload an analysis",
    features: [
      {
        title: "Your family's analyses",
        description: "Upload PDF from any lab. We interpret biomarkers for who you want to care for.",
      },
      {
        title: "Wellness overview",
        description: "Biological age, overall status and areas where your circle can improve habits.",
      },
      {
        title: "Findings that matter",
        description: "What matters in each report, explained so you can support your loved ones better.",
      },
      {
        title: "Personalized habits",
        description: "Lifestyle, nutrition, exercise and supplementation recommendations for your family.",
      },
      {
        title: "Support in your care",
        description: "Andy, your Cyrqle copilot, helps you understand each report and what to do for them.",
      },
    ],
  },
  ctaBanner: {
    title: "Upload the analysis of who you want to care for and discover how to improve their wellness.",
    cta: "Start now",
    imageAlt: "Family in a bright setting, sense of shared care and wellness.",
  },
  upload: {
    title: "Upload your report",
    subtitle: "Drop your PDF here or pick it from your device.",
    drop: "Drop the PDF to start",
    choose: "Choose file",
    accepted: "PDF only · Max 10 MB",
    parsing: "Analyzing your report…",
    invalidType: "Only PDF files are accepted.",
    tooLarge: "File exceeds 10 MB.",
    networkError: "We couldn't reach the server.",
    corsHint:
      "Your browser is blocking the connection (CORS). You can try demo mode to see a sample interpretation.",
    timeout: "Your report is large, still processing. Try again in a few seconds.",
    unsupportedLab:
      "We don't recognize this lab yet. We currently support Echevarne, Pasteur and Álvarez — more coming soon.",
    tryDemo: "Try with demo data",
    cancel: "Cancel",
    retry: "Retry",
    privacy: "Your report is encrypted in transit and never stored without your consent.",
  },
  analyzing: {
    title: "Reading your report",
    phases: ["Reading the report…", "Detecting biomarkers…", "Reviewing your biomarkers…", "Building personalized recommendations…"],
    note: "This takes a few seconds. Please keep the tab open.",
  },
  results: {
    backToTop: "Back to top",
    newAnalysis: "Upload another analysis",
    download: "Download summary",
    summary: {
      overline: "Executive summary",
      titleHello: "Your reading, in one page",
      titleSub: "The signal of your analysis, plain and clear.",
      labDetected: "Detected laboratory",
      biomarkers: "Biomarkers read",
      inRange: "In range",
      altered: "Out of range",
      keyFindings: "Key findings",
      healthSnapshot: "Overall snapshot",
      healthSnapshotHint:
        "Indicator derived from the share of biomarkers in range. A complete assessment requires an account.",
      patient: "Patient",
      record: "Record №",
    },
    biomarkers: {
      overline: "Biomarkers",
      title: "Your biomarkers, grouped",
      subtitle: "Filter, search and understand each value in context.",
      search: "Search biomarker…",
      onlyAltered: "Out of range only",
      all: "All",
      empty: "No biomarkers match.",
      ref: "Reference range",
      normal: "Normal",
      high: "High",
      low: "Low",
    },
    algorithms: {
      overline: "Wellness evaluation",
      title: "What specialists see",
      subtitle: "Nine specialized algorithms interpret your data across wellness areas.",
      lifestyle: "Lifestyle",
      nutrition: "Nutrition",
      supplementation: "Supplementation",
      clinical: "Follow-up",
      exclusion: "Avoid",
      noRecs: "No specific recommendations.",
      seeMore: "See more",
      seeLess: "See less",
    },
    categories: {
      hematology: "Hematology",
      lipids: "Lipids",
      glucose: "Glucose",
      liver: "Liver function",
      kidney: "Kidney function",
      minerals: "Minerals",
      vitamins: "Vitamins",
      thyroid: "Thyroid",
      other: "Other",
    },
    algoNames: {
      glucose: "Glucose",
      liver: "Liver",
      anemia: "Anemia",
      cametabolism: "Calcium metabolism",
      cholesterol: "Cholesterol",
      magnesium: "Magnesium",
      zinc: "Zinc",
      vitaminB: "B vitamins",
      vitaminD: "Vitamin D",
    },
    disclaimer:
      "These recommendations do not replace consultation with your healthcare professional.",
  },
  conversion: {
    title: "Caring for your circle starts here",
    subtitle:
      "Create your free account to save your family's reports, compare them over time and build wellness habits for everyone.",
    bullets: [
      "Save your circle's analyses",
      "Compare biomarkers over time",
      "Wellness tracking and biological age",
      "Recommendations and reminders for your family",
      "Access to the Cyrqle team",
    ],
    primary: "Create free account",
    secondary: "Maybe later",
    saved: "The report will be saved to your account so you can keep caring for your circle.",
  },
  how: {
    overline: "How it works",
    title: "From your family's test to wellness habits in three steps",
    steps: [
      { n: "01", t: "Upload their report", d: "The lab PDF of who you want to care for. No new blood draws." },
      { n: "02", t: "We read every biomarker", d: "We detect each value, its unit and reference range automatically." },
      { n: "03", t: "We guide your care", d: "Specialists turn data into clear habits and wellness for your circle." },
    ],
  },
  trust: {
    overline: "Why Cyrqle",
    title: "Their data. Your care.",
    sub: "Cyrqle puts your family's health information within reach of who cares most for them: you. Transparent, reviewed by wellness professionals.",
    stats: [
      { value: "85%", label: "better understand their circle's health" },
      { value: "9", label: "specialized algorithms" },
      { value: "< 3 min", label: "to interpret each report" },
    ],
    pillars: [
      { t: "Validated by specialists", d: "Every algorithm is built and reviewed by healthcare professionals." },
      { t: "For the whole family", d: "One analysis per person, one place to care for your circle." },
      { t: "Designed to care", d: "Clear language so you know what to do for them, without jargon." },
    ],
  },
  labs: {
    overline: "Compatibility",
    title: "Labs we recognize today",
    sub: "Auto-detected formats. More labs and mobile photos coming soon.",
    soon: "Coming soon",
    names: ["Echevarne", "Pasteur", "Álvarez", "+ more labs", "JPG/PNG photos", "Mobile OCR"],
  },
  faq: {
    overline: "Frequently asked",
    title: "Before uploading a family analysis",
    items: [
      { q: "Is it safe to upload my family's analysis?", a: "Yes. The connection is encrypted and the PDF is not stored without consent. Without an account, data lives only in your browser." },
      { q: "Can I upload analyses for several people?", a: "Yes. With your account you can save and track reports for your circle: partner, parents, children." },
      { q: "Which labs are supported today?", a: "We auto-detect Echevarne, Pasteur and Álvarez. Coverage is expanding monthly." },
      { q: "Does it replace my family's doctor?", a: "No. Cyrqle interprets data for wellness; clinical decisions are always made by their healthcare professional." },
      { q: "Why do I need an account?", a: "To save your circle's reports, compare them and receive habit and wellness recommendations." },
    ],
  },
  supplements: {
    overline: "Wellness for your circle",
    title: "Supplementation tuned for them",
    subtitle:
      "A curated selection that connects with report findings. No magic claims — science-backed nutrients, clear doses, traceability to care better.",
    curated: "Curated picks",
    unlockTitle: "Build your full routine",
    unlockSub: "Create your account to see more options, compare brands and receive a step-by-step protocol.",
    unlockCta: "Unlock the full catalog",
    disclaimer:
      "Cyrqle does not sell or prescribe medicines. Suggestions are orientation and never replace consultation with a healthcare professional.",
    reasons: {
      glucose: "For your elevated basal glucose",
      cholesterol: "To optimize your lipid profile",
      vitaminD: "To correct your vitamin D deficit",
      magnesium: "For your low-end magnesium",
      zinc: "For your low plasma zinc",
      anemia: "To support red cells and iron stores",
      liver: "To support liver function",
      vitaminB: "To maintain your B vitamins",
      cametabolism: "For bone health and calcium metabolism",
      foundation: "Recommended baseline stack",
    },
  },
  emailCapture: {
    overline: "Wellness in your circle",
    title: "Your family, healthier every week.",
    subtitle:
      "Drop your email or download the app and we'll send recommendations on supplementation, nutrition, sport and wellness — based on your circle's reports.",
    pillars: ["Supplementation", "Nutrition", "Sport", "Wellness"],
    emailLabel: "Your email",
    emailPlaceholder: "you@email.com",
    submit: "I want them",
    appOptIn: "Yes, send me the app",
    appOptInSub: "You'll get an iOS and Android link with your account already linked.",
    privacy: "No spam. Unsubscribe in one click. Your family's reports stay yours.",
    invalid: "Invalid email.",
    networkError: "We couldn't send your request. Please try again in a few seconds.",
    success: "Done. You're on the list.",
    successTitle: "You're in.",
    successBody: "We'll be in touch shortly with your first personalized recommendation.",
  },
  savedBanner: {
    title: "You have a saved analysis",
    savedAgo: "ago",
    resume: "View the report",
    resumeShort: "View report",
  },
  chat: {
    launcherLabel: "Talk to Andy",
    title: "Andy · your Cyrqle copilot",
    subtitle: "Ask me about your circle's health and wellness habits",
    subtitleAnalysis: "I have your family's latest report — ask me anything",
    placeholder: "Type your question…",
    send: "Send",
    reset: "Restart conversation",
    askChip: "Ask Andy",
    greetingOverline: "Get started",
    greeting: "Hi, I'm Andy.",
    greetingWithAnalysis: "Hi, I'm Andy. I've already read your family's latest report.",
    greetingBody: "Ask me about a biomarker, request habits for your circle or tell me who you want to care for.",
    greetingBodyAnalysis:
      "I can summarize findings, explain a specific value or propose a 4-week wellness plan. Where do we start?",
    suggestions: [
      "Summarize this analysis",
      "What should I prioritize to care?",
      "4-week wellness plan",
      "What does high LDL mean?",
    ],
    disclaimer:
      "Andy is an orienting assistant. It does not replace consultation with a healthcare professional.",
    errors: {
      network: "Couldn't reach Andy. Please try again.",
    },
    prefill: {
      biomarker: "Tell me about my {name}: I'm at {value}{unit} (range {range}). What does it mean and what can I do?",
      algorithm: "Explain my {name} evaluation (code: {code}). What priority changes do you recommend?",
      supplement: "Why do you recommend {name}? How and when should I take it?",
    },
  },
  profileModal: {
    title: "Tell us a bit about you",
    subtitle:
      "We just need your age and, optionally, your biological sex to compute your biological age and personalize the reading.",
    ageLabel: "Your age",
    sexLabel: "Biological sex",
    sexMale: "Male",
    sexFemale: "Female",
    sexOther: "Other / prefer not to say",
    save: "Compute",
    skip: "Not now",
    ageInvalid: "Enter an age between 15 and 100.",
    privacy: "Stored only in your browser. We don't send it to any server.",
  },
  bioAge: {
    overline: "Biological age",
    biological: "Biological",
    real: "Actual",
    deltaYounger: "{years} years younger than your actual age",
    deltaOlder: "{years} years older than your actual age",
    deltaEqual: "Aligned with your actual age",
    methodology:
      "Estimated from your core biomarkers (LDL, glucose, GGT, vitamin D, iron, magnesium, hemoglobin). For orientation only.",
    emptyTitle: "Know your biological age",
    emptyBody:
      "Compare your actual age with what your biomarkers say. We only need your age.",
    computeCta: "Add my age",
    edit: "Edit my data",
  },
  summary: {
    overline: "Analysis summary",
    title: "Your reading, in one page",
    placeholder:
      "No automatic summary available yet. Review biomarkers and wellness evaluation below, or ask Andy.",
  },
  knowledge: {
    overline: "Knowledge center",
    title: "Learn to care better for your circle",
    subtitle:
      "Short articles on supplementation, nutrition, sport, wellness and the science behind your family's biomarkers.",
    readMore: "Read article",
    readMins: "min",
    categories: {
      all: "All",
      supplementation: "Supplementation",
      nutrition: "Nutrition",
      sport: "Sport",
      wellness: "Wellness",
      science: "Science",
    },
  },
  article: {
    back: "Back to knowledge center",
    loading: "Loading article…",
    notFound: "We could not find this article.",
    references: "References",
  },
  footer: {
    tagline: "Wellness for your family.",
    legal: "Legal notice",
    privacy: "Privacy",
    terms: "Terms",
    support: "Support",
    rights: "All rights reserved.",
    disclaimer:
      "Cyrqle provides orientation based on biomarkers for your circle's wellness. It is not a medical device and does not replace professional consultation.",
  },
};

const fr = {
  nav: { howItWorks: "Comment ça marche", science: "Science", labs: "Laboratoires", faq: "FAQ", upload: "Téléverser l'analyse", login: "Se connecter" },
  hero: {
    overline: "Bien-être pour votre cercle",
    titleA: "Prenez soin de la santé",
    titleB: "de ceux que vous aimez",
    subtitle:
      "Téléversez l'analyse de votre famille et, en moins de 3 minutes, recevez une lecture claire : biomarqueurs, habitudes et recommandations bien-être pour accompagner votre cercle en confiance.",
    primaryCta: "Téléverser le rapport",
    secondaryCta: "Voir la démo",
    uploadHint: "PDF · Jusqu'à 10 Mo · Echevarne, Pasteur ou Álvarez",
    rotators: ["Pour votre conjoint, vos parents, vos enfants.", "Sans nouveau laboratoire.", "Sans jargon médical opaque."],
    trust: "Des familles et professionnels du bien-être utilisent Cyrqle pour mieux prendre soin de leur cercle.",
    compatible: "Compatible avec les rapports de tout laboratoire",
    highlights: [
      { title: "Importez leur analyse", subtitle: "PDF de la personne que vous voulez protéger" },
      { title: "Lecture en minutes", subtitle: "Clarté pour agir sur leur bien-être" },
      { title: "Tout laboratoire", subtitle: "Avec l'analyse qu'ils ont déjà" },
    ],
  },
  partners: { subtitle: "Compatible avec les rapports de tout laboratoire" },
  reportDetails: {
    overline: "Pour votre famille",
    title: "Ce que vous obtenez pour prendre soin des vôtres",
    description:
      "Nous interprétons les analyses de votre cercle et transformons les résultats en informations claires pour améliorer leur bien-être et leurs habitudes au quotidien.",
    indicated: "Les données sont déjà là. Cyrqle vous aide à mieux prendre soin de ceux qui comptent.",
    stat1Value: "< 3",
    stat1Label: "minutes pour votre rapport",
    stat2Value: "8+",
    stat2Label: "catégories de santé",
    stat3Value: "100%",
    stat3Label: "compatible avec tout labo",
    systemsTitle: "Catégories analysées",
    systems: ["Santé métabolique", "Santé cardiovasculaire", "Fonction rénale", "Fonction hépatique", "Inflammation", "Vitamines", "Santé hormonale", "Santé osseuse"],
    cta: "Importer mon analyse",
  },
  productPreview: {
    overline: "Aperçu",
    title: "Leur santé, interprétée pour que vous puissiez la protéger",
    subtitle: "Biomarqueurs groupés, conclusions clés et recommandations bien-être pour accompagner votre famille avec des actions concrètes.",
    quote: "Cyrqle traduit le langage du laboratoire en habitudes, nutrition et bien-être pour le cercle qui compte le plus pour vous.",
    quoteAuthor: "Équipe Cyrqle",
  },
  whatsIncluded: {
    overline: "Que comprend-il",
    title: "Tout pour le bien-être de votre cercle",
    subtitle: "et agir avec clarté dès le premier rapport.",
    cta: "Importer une analyse",
    features: [
      { title: "Analyses de votre famille", description: "PDF de tout laboratoire. Nous interprétons les biomarqueurs de ceux que vous voulez protéger." },
      { title: "Panorama bien-être", description: "Âge biologique, état général et domaines où votre cercle peut améliorer ses habitudes." },
      { title: "Conclusions qui comptent", description: "L'essentiel de chaque analyse, expliqué pour mieux accompagner vos proches." },
      { title: "Habitudes personnalisées", description: "Recommandations mode de vie, nutrition, exercice et supplémentation pour votre famille." },
      { title: "Accompagnement dans votre soin", description: "Andy, votre copilote Cyrqle, vous aide à comprendre chaque rapport et quoi faire pour eux." },
    ],
  },
  ctaBanner: {
    title: "Importez l'analyse de la personne que vous voulez protéger et découvrez comment améliorer son bien-être.",
    cta: "Commencer maintenant",
    imageAlt: "Famille dans un cadre lumineux, sensation de soin et bien-être partagé.",
  },
  upload: {
    title: "Téléversez votre rapport",
    subtitle: "Glissez le PDF ici ou sélectionnez-le depuis votre appareil.",
    drop: "Déposez le PDF pour commencer",
    choose: "Choisir un fichier",
    accepted: "PDF uniquement · Max 10 Mo",
    parsing: "Analyse en cours…",
    invalidType: "Seuls les fichiers PDF sont acceptés.",
    tooLarge: "Le fichier dépasse 10 Mo.",
    networkError: "Impossible de joindre le serveur.",
    corsHint: "Votre navigateur bloque la connexion (CORS). Essayez le mode démo pour voir un exemple.",
    timeout: "Votre rapport est volumineux, traitement en cours. Réessayez dans quelques secondes.",
    unsupportedLab: "Ce laboratoire n'est pas encore reconnu. Aujourd'hui : Echevarne, Pasteur et Álvarez.",
    tryDemo: "Essayer avec des données démo",
    cancel: "Annuler",
    retry: "Réessayer",
    privacy: "Votre rapport est chiffré pendant le transfert et n'est jamais stocké sans votre accord.",
  },
  analyzing: {
    title: "Lecture de votre rapport",
    phases: ["Lecture du rapport…", "Détection des biomarqueurs…", "Analyse de vos biomarqueurs…", "Création de recommandations personnalisées…"],
    note: "Quelques secondes. Gardez l'onglet ouvert.",
  },
  results: {
    backToTop: "Retour en haut",
    newAnalysis: "Téléverser une autre analyse",
    download: "Télécharger le résumé",
    summary: {
      overline: "Résumé exécutif",
      titleHello: "Votre lecture, en une page",
      titleSub: "L'essentiel de votre analyse, sans détours.",
      labDetected: "Laboratoire détecté",
      biomarkers: "Biomarqueurs lus",
      inRange: "Dans la norme",
      altered: "Hors norme",
      keyFindings: "Points clés",
      healthSnapshot: "Aperçu global",
      healthSnapshotHint:
        "Indicateur calculé à partir du pourcentage de biomarqueurs dans la norme. Une évaluation complète nécessite un compte.",
      patient: "Patient",
      record: "N° de dossier",
    },
    biomarkers: {
      overline: "Biomarqueurs",
      title: "Vos biomarqueurs, regroupés",
      subtitle: "Filtrez, cherchez, comprenez chaque valeur en contexte.",
      search: "Chercher un biomarqueur…",
      onlyAltered: "Hors norme seulement",
      all: "Tous",
      empty: "Aucun biomarqueur ne correspond.",
      ref: "Plage de référence",
      normal: "Normal",
      high: "Élevé",
      low: "Bas",
    },
    algorithms: {
      overline: "Évaluation bien-être",
      title: "Ce que voient les spécialistes",
      subtitle: "Neuf algorithmes spécialisés interprètent vos données.",
      lifestyle: "Hygiène de vie",
      nutrition: "Nutrition",
      supplementation: "Supplémentation",
      clinical: "Suivi",
      exclusion: "À éviter",
      noRecs: "Aucune recommandation spécifique.",
      seeMore: "Voir plus",
      seeLess: "Voir moins",
    },
    categories: {
      hematology: "Hématologie",
      lipids: "Lipides",
      glucose: "Glucose",
      liver: "Fonction hépatique",
      kidney: "Fonction rénale",
      minerals: "Minéraux",
      vitamins: "Vitamines",
      thyroid: "Thyroïde",
      other: "Autres",
    },
    algoNames: {
      glucose: "Glucose",
      liver: "Foie",
      anemia: "Anémie",
      cametabolism: "Métabolisme du calcium",
      cholesterol: "Cholestérol",
      magnesium: "Magnésium",
      zinc: "Zinc",
      vitaminB: "Vitamines B",
      vitaminD: "Vitamine D",
    },
    disclaimer:
      "Ces recommandations ne remplacent pas une consultation avec votre professionnel de santé.",
  },
  conversion: {
    title: "Prendre soin de votre cercle commence ici",
    subtitle:
      "Créez votre compte gratuit pour sauvegarder les rapports de votre famille, les comparer dans le temps et construire des habitudes de bien-être pour tous.",
    bullets: [
      "Sauvegardez les analyses de votre cercle",
      "Comparez les biomarqueurs dans le temps",
      "Suivi bien-être et âge biologique",
      "Recommandations et rappels pour votre famille",
      "Accès à l'équipe Cyrqle",
    ],
    primary: "Créer un compte gratuit",
    secondary: "Plus tard",
    saved: "Le rapport sera sauvegardé dans votre compte pour continuer à prendre soin de votre cercle.",
  },
  how: {
    overline: "Comment ça marche",
    title: "De l'analyse de votre famille aux habitudes de bien-être en trois étapes",
    steps: [
      { n: "01", t: "Téléversez leur rapport", d: "Le PDF du laboratoire de la personne que vous voulez protéger. Sans nouvelle prise de sang." },
      { n: "02", t: "Nous lisons chaque biomarqueur", d: "Nous détectons chaque valeur, unité et plage de référence." },
      { n: "03", t: "Nous vous orientons pour prendre soin", d: "Des spécialistes transforment les données en habitudes et bien-être clairs pour votre cercle." },
    ],
  },
  trust: {
    overline: "Pourquoi Cyrqle",
    title: "Leurs données. Votre soin.",
    sub: "Cyrqle place l'information santé de votre famille à portée de celui qui les protège le plus : vous. Transparent, revu par des professionnels du bien-être.",
    stats: [
      { value: "85%", label: "comprennent mieux la santé de leur cercle" },
      { value: "9", label: "algorithmes spécialisés" },
      { value: "< 3 min", label: "pour interpréter chaque rapport" },
    ],
    pillars: [
      { t: "Validé par des spécialistes", d: "Chaque algorithme est construit et revu par des professionnels de santé." },
      { t: "Pour toute la famille", d: "Une analyse par personne, un même endroit pour prendre soin de votre cercle." },
      { t: "Conçu pour prendre soin", d: "Langage clair pour savoir quoi faire pour eux, sans jargon." },
    ],
  },
  labs: {
    overline: "Compatibilité",
    title: "Laboratoires reconnus aujourd'hui",
    sub: "Formats détectés automatiquement. Plus de labos et photos mobiles arrivent.",
    soon: "Bientôt",
    names: ["Echevarne", "Pasteur", "Álvarez", "+ d'autres labos", "Photos JPG/PNG", "OCR mobile"],
  },
  faq: {
    overline: "Questions fréquentes",
    title: "Avant de téléverser une analyse familiale",
    items: [
      { q: "Est-ce sûr de téléverser l'analyse de ma famille ?", a: "Oui. La connexion est chiffrée et le PDF n'est pas stocké sans accord. Sans compte, les données restent dans votre navigateur." },
      { q: "Puis-je téléverser des analyses de plusieurs personnes ?", a: "Oui. Avec votre compte vous pouvez sauvegarder et suivre les rapports de votre cercle : conjoint, parents, enfants." },
      { q: "Quels laboratoires sont supportés aujourd'hui ?", a: "Echevarne, Pasteur et Álvarez sont détectés automatiquement. La couverture s'élargit chaque mois." },
      { q: "Cela remplace-t-il le médecin de ma famille ?", a: "Non. Cyrqle interprète les données pour le bien-être ; les décisions cliniques restent celles de leur professionnel de santé." },
      { q: "Pourquoi un compte ?", a: "Pour sauvegarder les rapports de votre cercle, les comparer et recevoir des recommandations d'habitudes et de bien-être." },
    ],
  },
  supplements: {
    overline: "Bien-être pour votre cercle",
    title: "Une supplémentation adaptée pour eux",
    subtitle:
      "Une sélection qui se connecte aux conclusions du rapport. Pas de promesses magiques — nutriments validés, doses claires, traçabilité pour mieux prendre soin.",
    curated: "Sélection curatée",
    unlockTitle: "Construisez votre routine complète",
    unlockSub: "Créez votre compte pour voir plus d'options, comparer les marques et recevoir un protocole pas à pas.",
    unlockCta: "Débloquer le catalogue complet",
    disclaimer:
      "Cyrqle ne vend ni ne prescrit de médicaments. Les suggestions sont indicatives et ne remplacent jamais l'avis d'un professionnel de santé.",
    reasons: {
      glucose: "Pour votre glucose basal élevé",
      cholesterol: "Pour optimiser votre profil lipidique",
      vitaminD: "Pour corriger votre déficit en vitamine D",
      magnesium: "Pour votre magnésium en limite basse",
      zinc: "Pour votre zinc plasmatique bas",
      anemia: "Pour soutenir vos globules rouges et réserves de fer",
      liver: "Pour soutenir votre foie",
      vitaminB: "Pour maintenir vos vitamines B",
      cametabolism: "Pour votre santé osseuse et le métabolisme du calcium",
      foundation: "Stack de base recommandé",
    },
  },
  emailCapture: {
    overline: "Bien-être dans votre cercle",
    title: "Votre famille, plus saine chaque semaine.",
    subtitle:
      "Laissez-nous votre email ou téléchargez l'app et nous enverrons des recommandations de supplémentation, nutrition, sport et bien-être — basées sur les rapports de votre cercle.",
    pillars: ["Supplémentation", "Nutrition", "Sport", "Bien-être"],
    emailLabel: "Votre email",
    emailPlaceholder: "vous@email.com",
    submit: "Je les veux",
    appOptIn: "Oui, envoyez-moi l'app",
    appOptInSub: "Vous recevrez un lien iOS et Android avec votre compte déjà lié.",
    privacy: "Pas de spam. Désabonnement en un clic. Les rapports de votre famille restent à vous.",
    invalid: "Email invalide.",
    networkError: "Impossible d'envoyer votre demande. Réessayez dans quelques secondes.",
    success: "C'est fait. Vous êtes dans la liste.",
    successTitle: "Vous y êtes.",
    successBody: "Nous vous écrirons bientôt avec votre première recommandation.",
  },
  savedBanner: {
    title: "Vous avez une analyse sauvegardée",
    savedAgo: "il y a",
    resume: "Voir le rapport",
    resumeShort: "Voir rapport",
  },
  chat: {
    launcherLabel: "Parler à Andy",
    title: "Andy · votre copilote Cyrqle",
    subtitle: "Posez-moi des questions sur la santé de votre cercle et les habitudes de bien-être",
    subtitleAnalysis: "J'ai le dernier rapport de votre famille — demandez-moi tout",
    placeholder: "Tapez votre question…",
    send: "Envoyer",
    reset: "Redémarrer la conversation",
    askChip: "Demander à Andy",
    greetingOverline: "Commencer",
    greeting: "Salut, je suis Andy.",
    greetingWithAnalysis: "Salut, je suis Andy. J'ai lu le dernier rapport de votre famille.",
    greetingBody:
      "Demandez-moi un biomarqueur, des habitudes pour votre cercle ou qui vous voulez protéger.",
    greetingBodyAnalysis:
      "Je peux résumer les résultats, expliquer une valeur ou proposer un plan de bien-être de 4 semaines. On commence par où ?",
    suggestions: [
      "Résume cette analyse",
      "Que prioriser pour prendre soin ?",
      "Plan bien-être 4 semaines",
      "Pourquoi le LDL est élevé ?",
    ],
    disclaimer:
      "Andy est un assistant d'orientation. Il ne remplace pas l'avis d'un professionnel de santé.",
    errors: {
      network: "Impossible de joindre Andy. Réessayez.",
    },
    prefill: {
      biomarker: "Parlez-moi de mon {name} : je suis à {value}{unit} (plage {range}). Que signifie cela et que puis-je faire ?",
      algorithm: "Expliquez mon évaluation de {name} (code : {code}). Quels changements prioritaires recommandez-vous ?",
      supplement: "Pourquoi recommandez-vous {name} ? Comment et quand le prendre ?",
    },
  },
  profileModal: {
    title: "Parlez-nous un peu de vous",
    subtitle:
      "Nous avons seulement besoin de votre âge et, optionnellement, de votre sexe biologique pour calculer votre âge biologique et personnaliser la lecture.",
    ageLabel: "Votre âge",
    sexLabel: "Sexe biologique",
    sexMale: "Homme",
    sexFemale: "Femme",
    sexOther: "Autre / Je préfère ne pas le dire",
    save: "Calculer",
    skip: "Pas maintenant",
    ageInvalid: "Entrez un âge entre 15 et 100.",
    privacy: "Enregistré uniquement dans votre navigateur. Nous ne l'envoyons à aucun serveur.",
  },
  bioAge: {
    overline: "Âge biologique",
    biological: "Biologique",
    real: "Réel",
    deltaYounger: "{years} ans de moins que votre âge réel",
    deltaOlder: "{years} ans de plus que votre âge réel",
    deltaEqual: "Aligné avec votre âge réel",
    methodology:
      "Estimation basée sur vos biomarqueurs principaux (LDL, glucose, GGT, vitamine D, fer, magnésium, hémoglobine). À titre indicatif.",
    emptyTitle: "Découvrez votre âge biologique",
    emptyBody:
      "Comparez votre âge réel avec ce que indiquent vos biomarqueurs. Nous avons seulement besoin de votre âge.",
    computeCta: "Ajouter mon âge",
    edit: "Modifier mes données",
  },
  summary: {
    overline: "Résumé de l'analyse",
    title: "Votre lecture, en une page",
    placeholder:
      "Pas encore de résumé automatique pour ce rapport. Consultez les biomarqueurs et l'évaluation bien-être ci-dessous, ou demandez à Andy.",
  },
  knowledge: {
    overline: "Centre de connaissances",
    title: "Apprenez à mieux prendre soin de votre cercle",
    subtitle:
      "Articles courts sur supplémentation, nutrition, sport, bien-être et la science derrière les biomarqueurs de votre famille.",
    readMore: "Lire l'article",
    readMins: "min",
    categories: {
      all: "Tout",
      supplementation: "Supplémentation",
      nutrition: "Nutrition",
      sport: "Sport",
      wellness: "Bien-être",
      science: "Science",
    },
  },
  article: {
    back: "Retour au centre de connaissances",
    loading: "Chargement de l'article…",
    notFound: "Article introuvable.",
    references: "Références",
  },
  footer: {
    tagline: "Bien-être pour votre famille.",
    legal: "Mentions légales",
    privacy: "Confidentialité",
    terms: "Conditions",
    support: "Support",
    rights: "Tous droits réservés.",
    disclaimer:
      "Cyrqle fournit une orientation basée sur les biomarqueurs pour le bien-être de votre cercle. Ce n'est pas un dispositif médical et ne remplace pas une consultation professionnelle.",
  },
};

const ca = {
  nav: { howItWorks: "Com funciona", science: "Ciència", labs: "Laboratoris", faq: "Preguntes", upload: "Pujar anàlisi", login: "Entrar" },
  hero: {
    overline: "Benestar per al teu cercle",
    titleA: "Cuida la salut",
    titleB: "de qui més estimes",
    subtitle:
      "Puja l'anàlisi de la teva família i, en menys de 3 minuts, rep una lectura clara: biomarcadors, hàbits i recomanacions de benestar per acompanyar el teu cercle amb confiança.",
    primaryCta: "Pujar informe",
    secondaryCta: "Veure demo",
    uploadHint: "PDF · Fins a 10 MB · Echevarne, Pasteur o Álvarez",
    rotators: ["Per la teva parella, els teus pares, els teus fills.", "Sense un altre laboratori.", "Sense llenguatge mèdic opac."],
    trust: "Famílies i professionals del benestar ja usen Cyrqle per cuidar millor el seu cercle.",
    compatible: "Compatible amb informes de qualsevol laboratori",
    highlights: [
      { title: "Puja la seva anàlisi", subtitle: "PDF de qui vols cuidar" },
      { title: "Lectura en minuts", subtitle: "Claredat per actuar en el seu benestar" },
      { title: "Qualsevol laboratori", subtitle: "Amb l'anàlisi que ja tenen" },
    ],
  },
  partners: { subtitle: "Compatible amb informes de qualsevol laboratori" },
  reportDetails: {
    overline: "Per la teva família",
    title: "El que obtens per cuidar dels teus",
    description:
      "Interpretem les anàlisis del teu cercle i convertim els resultats en informació clara per millorar el seu benestar i els seus hàbits dia a dia.",
    indicated: "Les dades ja hi són. Cyrqle t'ajuda a cuidar millor de qui t'importa.",
    stat1Value: "< 3",
    stat1Label: "minuts per al teu informe",
    stat2Value: "8+",
    stat2Label: "categories de salut",
    stat3Value: "100%",
    stat3Label: "compatible amb qualsevol lab",
    systemsTitle: "Categories analitzades",
    systems: ["Salut metabolica", "Salut cardiovascular", "Funcio renal", "Funcio hepatica", "Inflamacio", "Vitamines", "Salut hormonal", "Salut osia"],
    cta: "Pujar la meva analisi",
  },
  productPreview: {
    overline: "Vista previa",
    title: "La seva salut, interpretada perquè puguis cuidar-la",
    subtitle: "Biomarcadors agrupats, troballes clau i recomanacions de benestar per acompanyar la teva família amb decisions concretes.",
    quote: "Cyrqle tradueix el llenguatge del laboratori en hàbits, nutrició i benestar per al cercle que més t'importa.",
    quoteAuthor: "Equip Cyrqle",
  },
  whatsIncluded: {
    overline: "Que inclou",
    title: "Tot per al benestar del teu cercle",
    subtitle: "i per actuar amb claredat des del primer informe.",
    cta: "Pujar una anàlisi",
    features: [
      { title: "Anàlisis de la teva família", description: "PDF de qualsevol laboratori. Interpretem els biomarcadors de qui vols cuidar." },
      { title: "Panorama de benestar", description: "Edat biològica, estat general i àrees on el teu cercle pot millorar hàbits." },
      { title: "Troballes que importen", description: "L'essencial de cada anàlisi, explicat per acompanyar millor els teus." },
      { title: "Hàbits personalitzats", description: "Recomanacions d'estil de vida, nutrició, exercici i suplementació per la teva família." },
      { title: "Acompanyament en el teu cuidat", description: "Andy, el teu copilot Cyrqle, t'ajuda a entendre cada informe i què fer per ells." },
    ],
  },
  ctaBanner: {
    title: "Puja l'anàlisi de qui vols cuidar i descobreix com millorar el seu benestar.",
    cta: "Començar ara",
    imageAlt: "Família en un entorn lluminós, sensació de cuidat i benestar compartit.",
  },
  upload: {
    title: "Puja el teu informe",
    subtitle: "Arrossega el PDF aquí o tria'l del teu dispositiu.",
    drop: "Deixa anar el PDF per començar",
    choose: "Triar fitxer",
    accepted: "Només PDF · Màx. 10 MB",
    parsing: "Analitzant el teu informe…",
    invalidType: "Només acceptem fitxers PDF.",
    tooLarge: "El fitxer supera els 10 MB.",
    networkError: "No hem pogut connectar amb el servidor.",
    corsHint:
      "El teu navegador està bloquejant la connexió (CORS). Pots provar el mode demo per veure un exemple.",
    timeout: "L'informe és gran, encara s'està processant. Torna-ho a provar en uns segons.",
    unsupportedLab:
      "Encara no reconeixem aquest laboratori. Avui suportem Echevarne, Pasteur i Álvarez.",
    tryDemo: "Provar amb dades demo",
    cancel: "Cancel·lar",
    retry: "Tornar a provar",
    privacy: "El teu informe es xifra en el trànsit i no s'emmagatzema sense consentiment.",
  },
  analyzing: {
    title: "Llegint el teu informe",
    phases: ["Llegint l'informe…", "Detectant biomarcadors…", "Analitzant els teus biomarcadors…", "Generant recomanacions personalitzades…"],
    note: "Triga uns segons. No tanquis la pestanya.",
  },
  results: {
    backToTop: "Tornar a dalt",
    newAnalysis: "Pujar una altra anàlisi",
    download: "Descarregar resum",
    summary: {
      overline: "Resum executiu",
      titleHello: "La teva lectura, en una pàgina",
      titleSub: "El més rellevant de la teva anàlisi, sense tecnicismes.",
      labDetected: "Laboratori detectat",
      biomarkers: "Biomarcadors llegits",
      inRange: "Dins de rang",
      altered: "Alterats",
      keyFindings: "Troballes clau",
      healthSnapshot: "Estat general",
      healthSnapshotHint:
        "Indicador derivat del percentatge de biomarcadors dins de rang. Una valoració completa requereix compte.",
      patient: "Pacient",
      record: "Núm. d'expedient",
    },
    biomarkers: {
      overline: "Biomarcadors",
      title: "Els teus biomarcadors, agrupats",
      subtitle: "Filtra, busca i entén cada valor en el seu context.",
      search: "Cercar biomarcador…",
      onlyAltered: "Només alterats",
      all: "Tots",
      empty: "No hi ha biomarcadors que coincideixin.",
      ref: "Rang de referència",
      normal: "Normal",
      high: "Alt",
      low: "Baix",
    },
    algorithms: {
      overline: "Avaluació de benestar",
      title: "El que veuen els especialistes",
      subtitle: "Nou algoritmes especialitzats interpreten les teves dades.",
      lifestyle: "Estil de vida",
      nutrition: "Nutrició",
      supplementation: "Suplementació",
      clinical: "Seguiment",
      exclusion: "A evitar",
      noRecs: "Sense recomanacions específiques.",
      seeMore: "Veure més",
      seeLess: "Veure menys",
    },
    categories: {
      hematology: "Hematologia",
      lipids: "Lípids",
      glucose: "Glucosa",
      liver: "Funció hepàtica",
      kidney: "Funció renal",
      minerals: "Minerals",
      vitamins: "Vitamines",
      thyroid: "Tiroide",
      other: "Altres",
    },
    algoNames: {
      glucose: "Glucosa",
      liver: "Fetge",
      anemia: "Anèmia",
      cametabolism: "Metabolisme del calci",
      cholesterol: "Colesterol",
      magnesium: "Magnesi",
      zinc: "Zinc",
      vitaminB: "Vitamines del grup B",
      vitaminD: "Vitamina D",
    },
    disclaimer:
      "Aquestes recomanacions no substitueixen la consulta amb el teu professional sanitari.",
  },
  conversion: {
    title: "Cuidar del teu cercle comença aquí",
    subtitle:
      "Crea el teu compte gratuït per guardar els informes de la teva família, comparar-los en el temps i construir hàbits de benestar per tots.",
    bullets: [
      "Guarda les anàlisis del teu cercle",
      "Compara biomarcadors al llarg del temps",
      "Seguiment de benestar i edat biològica",
      "Recomanacions i recordatoris per la teva família",
      "Accés a l'equip Cyrqle",
    ],
    primary: "Crear compte gratuït",
    secondary: "Potser més tard",
    saved: "L'informe quedarà desat al teu compte per continuar cuidant del teu cercle.",
  },
  how: {
    overline: "Com funciona",
    title: "De l'anàlisi de la teva família a hàbits de benestar en tres passos",
    steps: [
      { n: "01", t: "Puges el seu informe", d: "El PDF del laboratori de qui vols cuidar. Sense noves extraccions." },
      { n: "02", t: "Llegim cada biomarcador", d: "Detectem cada valor, unitat i rang de referència." },
      { n: "03", t: "T'orientem per cuidar", d: "Especialistes converteixen les dades en hàbits i benestar clars per al teu cercle." },
    ],
  },
  trust: {
    overline: "Per què Cyrqle",
    title: "Les seves dades. El teu cuidat.",
    sub: "Cyrqle posa la informació de salut de la teva família a l'abast de qui més els cuida: tu. Transparent, revisat per professionals del benestar.",
    stats: [
      { value: "85%", label: "entenen millor la salut del seu cercle" },
      { value: "9", label: "algoritmes especialitzats" },
      { value: "< 3 min", label: "per interpretar cada informe" },
    ],
    pillars: [
      { t: "Validat per especialistes", d: "Cada algoritme està construït i revisat per professionals sanitaris." },
      { t: "Per tota la família", d: "Una anàlisi per persona, un mateix lloc per cuidar del teu cercle." },
      { t: "Dissenyat per cuidar", d: "Llenguatge clar per saber què fer per ells, sense tecnicismes." },
    ],
  },
  labs: {
    overline: "Compatibilitat",
    title: "Laboratoris reconeguts avui",
    sub: "Detectem el format automàticament. Aviat ampliarem a més laboratoris i a fotos.",
    soon: "Aviat",
    names: ["Echevarne", "Pasteur", "Álvarez", "+ més laboratoris", "Fotos JPG/PNG", "OCR mòbil"],
  },
  faq: {
    overline: "Preguntes freqüents",
    title: "Abans de pujar una anàlisi de la teva família",
    items: [
      { q: "És segur pujar l'anàlisi de la meva família?", a: "Sí. La connexió és xifrada i el PDF no s'emmagatzema sense consentiment." },
      { q: "Puc pujar anàlisis de diverses persones?", a: "Sí. Amb el teu compte pots guardar i seguir els informes del teu cercle: parella, pares, fills." },
      { q: "Quins laboratoris admeteu?", a: "Avui detectem Echevarne, Pasteur i Álvarez automàticament." },
      { q: "Substitueix el metge de la meva família?", a: "No. Cyrqle interpreta les dades per al benestar; les decisions clíniques són del seu professional." },
      { q: "Per què necessito compte?", a: "Per guardar els informes del teu cercle, comparar-los i rebre recomanacions d'hàbits i benestar." },
    ],
  },
  supplements: {
    overline: "Benestar per al teu cercle",
    title: "Suplementació afinada per a ells",
    subtitle:
      "Una selecció que connecta amb les troballes de l'informe. Sense promeses màgiques: nutrients amb suport, dosis clares i traçabilitat per cuidar millor.",
    curated: "Selecció curada",
    unlockTitle: "Construeix la teva rutina completa",
    unlockSub: "Crea el teu compte per veure més opcions, comparar marques i rebre un protocol pas a pas.",
    unlockCta: "Desbloquejar catàleg complet",
    disclaimer:
      "Cyrqle no ven ni prescriu medicaments. Els suggeriments són orientatius i no substitueixen la consulta del professional sanitari.",
    reasons: {
      glucose: "Per a la teva glucosa basal elevada",
      cholesterol: "Per optimitzar el teu perfil lipídic",
      vitaminD: "Per corregir el teu dèficit de vitamina D",
      magnesium: "Per al teu magnesi al límit inferior",
      zinc: "Per al teu zinc plasmàtic baix",
      anemia: "Per donar suport a la sèrie vermella",
      liver: "Per donar suport al teu fetge",
      vitaminB: "Per mantenir les teves vitamines B",
      cametabolism: "Per a la teva salut òssia i metabolisme del calci",
      foundation: "Stack bàsic recomanat",
    },
  },
  emailCapture: {
    overline: "Benestar al teu cercle",
    title: "La teva família, més sana cada setmana.",
    subtitle:
      "Deixa'ns el teu email o baixa l'app i començarem a enviar-te recomanacions de suplementació, nutrició, esport i benestar — basades en els informes del teu cercle.",
    pillars: ["Suplementació", "Nutrició", "Esport", "Benestar"],
    emailLabel: "El teu email",
    emailPlaceholder: "tu@correu.com",
    submit: "Les vull rebre",
    appOptIn: "Sí, vull baixar l'app",
    appOptInSub: "Rebràs un enllaç per iOS i Android amb el teu compte enllaçat.",
    privacy: "Sense spam. Pots donar-te de baixa amb un clic. Els informes de la teva família segueixen sent teus.",
    invalid: "Email no vàlid.",
    networkError: "No hem pogut enviar la teva sol·licitud. Torna-ho a provar d'aquí uns segons.",
    success: "Fet. T'hem guardat a la llista.",
    successTitle: "Ja hi ets.",
    successBody: "Et contactarem aviat amb la teva primera recomanació.",
  },
  savedBanner: {
    title: "Tens una anàlisi desada",
    savedAgo: "fa",
    resume: "Veure l'informe",
    resumeShort: "Veure informe",
  },
  chat: {
    launcherLabel: "Parla amb l'Andy",
    title: "Andy · el teu copilot Cyrqle",
    subtitle: "Pregunta'm sobre la salut del teu cercle i hàbits de benestar",
    subtitleAnalysis: "Conec l'últim informe de la teva família — pregunta'm el que necessitis",
    placeholder: "Escriu la teva pregunta…",
    send: "Enviar",
    reset: "Reiniciar conversa",
    askChip: "Pregunta a l'Andy",
    greetingOverline: "Comencem",
    greeting: "Hola, sóc l'Andy.",
    greetingWithAnalysis: "Hola, sóc l'Andy. Ja he llegit l'últim informe de la teva família.",
    greetingBody:
      "Pregunta'm per un biomarcador, demana'm hàbits per al teu cercle o explica'm a qui vols cuidar.",
    greetingBodyAnalysis:
      "Puc resumir les troballes, explicar-te un valor concret o proposar-te un pla de benestar de 4 setmanes. Per on comencem?",
    suggestions: [
      "Resumeix aquesta anàlisi",
      "Què prioritzo per cuidar?",
      "Pla de benestar 4 setmanes",
      "Què vol dir LDL alt?",
    ],
    disclaimer:
      "L'Andy és un assistent orientatiu. No substitueix la consulta d'un professional sanitari.",
    errors: {
      network: "No hem pogut connectar amb l'Andy. Torna-ho a provar.",
    },
    prefill: {
      biomarker: "Explica'm sobre el meu {name}: estic a {value}{unit} (rang {range}). Què vol dir i què puc fer?",
      algorithm: "Explica'm la meva avaluació de {name} (codi: {code}). Quins canvis prioritaris em recomanes?",
      supplement: "Per què em recomanes {name}? Com i quan el prenc?",
    },
  },
  profileModal: {
    title: "Explica'ns una mica sobre tu",
    subtitle:
      "Només necessitem la teva edat i, opcionalment, el teu sexe biològic per calcular la teva edat biològica i personalitzar la lectura.",
    ageLabel: "La teva edat",
    sexLabel: "Sexe biològic",
    sexMale: "Home",
    sexFemale: "Dona",
    sexOther: "Altre / Prefereixo no dir-ho",
    save: "Calcular",
    skip: "Ara no",
    ageInvalid: "Introdueix una edat entre 15 i 100.",
    privacy: "Desat només al teu navegador. No l'enviem a cap servidor.",
  },
  bioAge: {
    overline: "Edat biològica",
    biological: "Biològica",
    real: "Real",
    deltaYounger: "{years} anys per sota de la teva edat real",
    deltaOlder: "{years} anys per sobre de la teva edat real",
    deltaEqual: "Alineada amb la teva edat real",
    methodology:
      "Estimació basada en els teus biomarcadors principals (LDL, glucosa, GGT, vitamina D, ferro, magnesi, hemoglobina). És orientativa.",
    emptyTitle: "Coneix la teva edat biològica",
    emptyBody:
      "Compara la teva edat real amb la que indiquen els teus biomarcadors. Només necessitem la teva edat.",
    computeCta: "Afegir la meva edat",
    edit: "Editar les meves dades",
  },
  summary: {
    overline: "Resum de l'anàlisi",
    title: "La teva lectura, en una pàgina",
    placeholder:
      "Encara no hi ha un resum automàtic per a aquest informe. Revisa els biomarcadors i l'avaluació de benestar a sota, o pregunta a l'Andy.",
  },
  knowledge: {
    overline: "Centre de coneixement",
    title: "Aprèn per cuidar millor del teu cercle",
    subtitle:
      "Articles breus sobre suplementació, nutrició, esport, benestar i la ciència darrere els biomarcadors de la teva família.",
    readMore: "Llegir article",
    readMins: "min",
    categories: {
      all: "Tot",
      supplementation: "Suplementació",
      nutrition: "Nutrició",
      sport: "Esport",
      wellness: "Benestar",
      science: "Ciència",
    },
  },
  article: {
    back: "Tornar al centre de coneixement",
    loading: "Carregant article…",
    notFound: "No hem trobat aquest article.",
    references: "Referències",
  },
  footer: {
    tagline: "Benestar per a la teva família.",
    legal: "Avís legal",
    privacy: "Privacitat",
    terms: "Termes",
    support: "Suport",
    rights: "Tots els drets reservats.",
    disclaimer:
      "Cyrqle ofereix informació orientativa basada en biomarcadors per al benestar del teu cercle. No és un dispositiu mèdic ni substitueix la consulta professional.",
  },
};

export const TRANSLATIONS = { es, en, fr, ca };
